import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { map, switchMap } from 'rxjs';

import { PrivaSelectOption } from '@priva/components/select';

import {
    selectProductBoxList,
    selectProductBoxListBuNumbers,
} from 'app/state/product-boxes/product-box.selectors';

import { Activity } from '../../features/activity-selector/models';
import { areProductBoxRegistrationArraysEqual } from '../../utils';
import { ProductBox, ProductBoxRegistration } from './models';

type ProductBoxState = {
    activity: Activity | undefined;
    productBoxRegistrations: ProductBoxRegistration[];
};

const initialState: ProductBoxState = {
    activity: undefined,
    productBoxRegistrations: [],
};

@Injectable()
export class ProductBoxStore extends ComponentStore<ProductBoxState> {
    private store = inject(Store);
    constructor() {
        super(initialState);
    }

    readonly productBoxRegistrations = this.selectSignal((state) => state.productBoxRegistrations);

    productBoxes$ = this.state$.pipe(
        switchMap(({ activity, productBoxRegistrations }) => {
            /*
                resultDeterminationType ByContainerAndProductBox is introduced in FS Performance > v4.11 and
                orderedProductBoxNumbers (multiple product boxes) is introduced in FS Performance > v4.6
                therefore, support is not required for FS Performance < v4.6
             */
            return activity?.orderedProductBoxNumbers && activity?.orderedProductBoxNumbers.length > 0
                ? this.store.pipe(
                      select(selectProductBoxListBuNumbers(activity.orderedProductBoxNumbers)),
                      map((productBoxList) =>
                          productBoxList.filter((productBox): productBox is ProductBox => !!productBox),
                      ),
                      map((productBoxList) => ({
                          activity,
                          productBoxes: productBoxList,
                          productBoxRegistrations,
                      })),
                  )
                : // Returns all product boxes when no default list is set in FS Portal
                  this.store.select(selectProductBoxList).pipe(
                      map((productBoxes) => ({
                          activity,
                          productBoxes: productBoxes,
                          productBoxRegistrations,
                      })),
                  );
        }),
        map(({ activity, productBoxes, productBoxRegistrations }) => {
            // Get list of product boxes either from orderedProductBoxNumbers / defaultProductBox or list from activity
            const privaSelectOption =
                productBoxRegistrations.length > 0
                    ? productBoxes.map(
                          (productBox): PrivaSelectOption => ({
                              label: productBox.name || 'Box ' + productBox.number,
                              value: productBox?.number?.toString() ?? '',
                              selected:
                                  productBox.number === Number(productBoxRegistrations[0].productBoxNumber),
                          }),
                      )
                    : productBoxes.map(
                          // Populate values for Priva dropdown
                          // Always display 1st item in the product box in dropdown
                          (productBox): PrivaSelectOption => ({
                              label: productBox.name || 'Box ' + productBox.number,
                              value: productBox?.number?.toString() ?? '',
                              selected: productBox.number === productBoxes[0].number,
                          }),
                      );
            return {
                activity: activity ?? null,
                privaSelectOption: privaSelectOption,
            };
        }),
    );

    updateActivity = this.updater((state, activity: Activity | undefined) => ({
        ...state,
        activity: activity,
    }));

    updateProductBoxRegistrations = this.updater(
        (state, productBoxRegistration: ProductBoxRegistration | undefined) => {
            const productBoxRegistrationList = productBoxRegistration ? [productBoxRegistration] : [];

            if (
                areProductBoxRegistrationArraysEqual(
                    productBoxRegistrationList,
                    state.productBoxRegistrations,
                )
            ) {
                // circuit breaker, returns old existing state if there's no change in values
                return state;
            }
            return {
                ...state,
                productBoxRegistrations: productBoxRegistration ? [productBoxRegistration] : [],
            };
        },
    );
}
